import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-hot-toast";
import { getErrorMessage } from "@/utils/error.utils";

import { Button } from "@/components/ui/button";
import { TextField } from "@/components/Textfield";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  useCreatePerformanceMetricMutation,
  useGetPerformanceMetricByIdQuery,
  useUpdatePerformanceMetricMutation,
} from "@/services/(company)/api.performance";
import { performanceMetricValidationSchema } from "@/validations/performance/schema";

type IFormValues = Yup.InferType<typeof performanceMetricValidationSchema>;

interface Props {
  selectedId: number | null;
  handleBack: () => void;
}

export const PerformanceMetricsForm = ({ selectedId, handleBack }: Props) => {
  const { t } = useTranslation(["common", "company"]);
  const { data: singleMetricData } = useGetPerformanceMetricByIdQuery(
    { metricId: String(selectedId) },
    { skip: !selectedId },
  );

  const [createMetric, { isLoading: isCreating }] =
    useCreatePerformanceMetricMutation();
  const [updateMetric, { isLoading: isUpdating }] =
    useUpdatePerformanceMetricMutation();

  const isLoading = isCreating || isUpdating;

  const formik = useFormik<IFormValues>({
    enableReinitialize: true,
    validationSchema: performanceMetricValidationSchema,
    initialValues: {
      title: singleMetricData?.data?.title ?? "",
      description: singleMetricData?.data?.description ?? "",
      isActive: singleMetricData?.data?.isActive ?? true,
    },
    onSubmit: async (values) => {
      const toastId = toast.loading(t("company:performance.processing"));
      try {
        let res;
        if (selectedId) {
          res = await updateMetric({
            metricId: String(selectedId),
            payload: values,
          }).unwrap();
        } else {
          res = await createMetric(values).unwrap();
        }
        toast.success(res.message, { id: toastId });
        handleBack();
      } catch (err: unknown) {
        toast.error(
          getErrorMessage(err, t("company:performance.something_went_wrong")),
          { id: toastId },
        );
      }
    },
  });

  return (
    <section className="flex flex-col gap-4 min-h-card-content-2">
      <form
        onSubmit={formik.handleSubmit}
        className="flex flex-1 flex-col gap-5 justify-between"
      >
        <div className="grid grid-cols-1 gap-5 w-full">
          <TextField
            id="title"
            label={t("company:performance.metric_title_label")}
            placeholder={t("common:placeholders.enter_metric_title")}
            name="title"
            type="text"
            formik={formik}
          />

          <Field>
            <FieldLabel>
              {t("company:performance.description_label_caps")}
            </FieldLabel>
            <Textarea
              className="min-h-36"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t("common:a11y.metric_description")}
            />
            {formik.touched.description && formik.errors.description && (
              <ErrorMessage
                text={formik.errors.description}
                className="text-sm!"
                wrapperClassName="items-start! justify-start! p-0! h-auto!"
              />
            )}
          </Field>

          <div className="flex gap-2 items-center">
            <Checkbox
              id="isActive"
              name="isActive"
              checked={formik.values.isActive}
              onCheckedChange={(checked) =>
                formik.setFieldValue("isActive", checked)
              }
            />
            <Label>{t("company:performance.mark_as_active")}</Label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            className="w-36"
          >
            {t("common:buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            className="w-36"
          >
            {selectedId
              ? isLoading
                ? t("company:performance.updating_cta")
                : t("company:performance.update_cta")
              : isLoading
                ? t("company:performance.saving_cta")
                : t("company:performance.save_cta")}
          </Button>
        </div>
      </form>
    </section>
  );
};
